import { z } from 'zod';

export const IntentLabel = z.enum(['chat', 'task', 'approval', 'correction']);
export type IntentLabel = z.infer<typeof IntentLabel>;

export const IntentResult = z.object({
  intent: IntentLabel,
  confidence: z.number().min(0).max(1).default(0.5),
  /** True when the message asks about TWO itself (identity, abilities, privacy, models). */
  about_two: z.boolean().default(false),
  reason: z.string().optional(),
});
export type IntentResult = z.infer<typeof IntentResult>;

export const INTENT_FALLBACK: IntentResult = { intent: 'chat', confidence: 0, about_two: false };

export const INTENT_SYSTEM_PROMPT = `You classify a single user message sent to TWO, a personal agent.
Return ONLY a JSON object, no prose, no code fences: {"intent": "...", "confidence": 0..1, "about_two": true|false, "reason": "short"}.
Intents:
- "chat": greeting, small talk, a question, or a statement that needs no action.
- "task": the user asks TWO to do or arrange something (remind, schedule, send, pay, book, find, create, generate).
- "approval": the user approves or rejects something TWO proposed (yes / go ahead / no / cancel / نعم / موافق / لا / ألغِ).
- "correction": the user corrects TWO or states a preference about how it should behave.
about_two: true only when the message asks about TWO itself: what it is, who made it, what it can or cannot do, how it works or which models it uses, privacy or what happens to the data. Otherwise false.
The message may be Arabic, English, or mixed.`;

/**
 * Parse the classifier output. Tolerates code fences, leading prose and trailing text;
 * anything unparseable becomes the fallback (intent "chat", confidence 0).
 */
export function parseIntent(raw: string): { result: IntentResult; ok: boolean } {
  const candidate = extractJsonObject(raw);
  if (!candidate) return { result: INTENT_FALLBACK, ok: false };
  try {
    const parsed = IntentResult.safeParse(JSON.parse(candidate));
    if (!parsed.success) return { result: INTENT_FALLBACK, ok: false };
    return { result: parsed.data, ok: true };
  } catch {
    return { result: INTENT_FALLBACK, ok: false };
  }
}

function extractJsonObject(raw: string): string | undefined {
  const unfenced = raw.replace(/```(?:json)?/gi, '').trim();
  const start = unfenced.indexOf('{');
  const end = unfenced.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) return undefined;
  return unfenced.slice(start, end + 1);
}
